import React, { useEffect, useState } from "react";
import AdminDashboard from "./admindashboard";
import axios from "../api/baseurl";
import { useNavigate, useParams } from "react-router-dom";
import "./Spinner.css";
import "./Loader.css";
import "./Stepper.css";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import { Modal, Button, Pagination } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaRegUserCircle } from "react-icons/fa";
import "./hm-theme.css";

function BookDoctorAppointment() {
  const { userid } = useParams();
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [step, setStep] = useState(1);
  const [loader, setLoader] = useState(true);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [paymentRequiredResponse, setPaymentRequiredResponse] = useState(true);
  const [employee, setEmployee] = useState(null);
  const navigate = useNavigate();
  const userRole = JSON.parse(localStorage.getItem("data"))?.role;
  const token = JSON.parse(localStorage.getItem("data"))?.token;

  useEffect(() => {
    if (searchTerm.trim()) {
      searchData(0);  
    } else {
      getData(currentPage);   
    }
  }, [searchTerm]); 

  const searchData = async (pageNumber = 0) => {
    setLoader(true);
    try {
      const searchUrl = `/api/doctordetails/searchDoctorsForAppointment`;
      const requestData = {
        searchTerm,
        companyId: employee?.companyId
      };
      const response = await axios.post(searchUrl, requestData, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      setDoctors(response.data.content);
      setCurrentPage(response.data.number);
      setTotalPages(response.data.totalPages);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  const getData = async (pageNumber = 0) => {
    setLoader(true);
    try {  
      const empRes = await axios.get(`/api/employeedetails/userid=${userid}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmployee(empRes.data);    
    
      const requestData = {
        companyId: empRes.data.companyId,
      };
      const trans = await axios.post(`/api/doctordetails/getAllDoctorsForAppointment`, requestData, {
        params: { page: pageNumber },
        headers: { Authorization: `Bearer ${token}` },
      });

      setDoctors(trans.data.content);
      setCurrentPage(trans.data.number);
      setTotalPages(trans.data.totalPages);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      handleError(error);
    }
  };

  function handleError(error) {
    if (error.response) {
      if (error.response.status === 401) {
        navigate(`/signin`);
        toast.error("Your session has expired. Please login.");
      } else if (error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
      } else if (error.response.data && error.response.data.code) {
        toast.error(error.response.data.code);
      } else {
        toast.error("An error occurred.");
      }
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error("An error occurred.");
    }
  }

  useEffect(() => {
    getData();
  }, []);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  
    if (searchTerm.trim()) {
      searchData(newPage);
    } else {
      getData(newPage);
    }
  };

  const handleBookAppointment = async () => {
    if (!appointmentDate) {
      toast.warn("Please select a date");
      return;
    }
   
    const selectedDate = new Date(appointmentDate);
    const currentDate = new Date();
    const oneDayFromNow = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate() + 1);
   
    if (userRole !== 'ROLE_SUPER' && userRole !== 'ROLE_ADMIN') {
      if (selectedDate < oneDayFromNow || selectedDate.getDay() === 0) {
        toast.warn("Please select a weekday other than Sunday and ensure the date is at least 1 day from today.");
        return;
      }
    }
   
    if (selectedDate.getDay() === 0) {
      toast.warn("Please select a weekday other than Sunday.");
      return;
    }

    setShowWarningModal(true);
  };

  const confirmBookAppointment = async () => {
    try {
      setLoader(true);
      await axios.post(
        "/api/doctorappointment/create",
        {
          employeeId: employee.id,
          doctorId: selectedDoctor.id,
          appointmentDate: appointmentDate,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success("Doctor Appointment Booked successfully");
      if (userRole === 'ROLE_EMPLOYEE') {
        navigate('/users/employee-appointments');
      } else {
        navigate(`/users/employee-profile/${userid}`);
      }
    } catch (error) {
      setLoader(false);
      handleError(error);
    } finally {
      setShowConfirmationModal(false);
      setShowWarningModal(false);
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 1:
        return (
          <div className="hm-card mb-4">
            <div className="hm-card-header d-flex align-items-center justify-content-between flex-wrap gap-3">
              <h3 className="hm-card-title m-0">
                <i className="material-icons">medical_services</i>
                Choose Doctor
              </h3>
              <div className="position-relative" style={{ width: "300px" }}>
                <input
                  type="search"
                  className="form-control ps-5"
                  placeholder="Search by Doctor name, city, pincode"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="position-absolute text-muted"
                  style={{ left: "14px", top: "50%", transform: "translateY(-50%)" }}
                />
              </div>
            </div>

            <div className="hm-card-body p-4">
              <div className="row g-4 mb-4">
                {doctors.length === 0 ? (
                  <div className="col-12">
                    <div className="hm-empty-state py-5">
                      <i className="material-icons">medical_services</i>
                      <p>No Doctors Available</p>
                    </div>
                  </div>
                ) : (
                  doctors.map((doctor) => {
                    const isSelected = selectedDoctor && selectedDoctor.id === doctor.id;
                    return (
                      <div className="col-md-6 col-lg-4" key={doctor.id}>
                        <div
                          className={`hm-card h-100 p-3 text-center border transition-all cursor-pointer ${
                            isSelected
                              ? "border-primary shadow"
                              : "border-light shadow-sm hover-shadow"
                          }`}
                          onClick={() => setSelectedDoctor(doctor)}
                          style={{
                            cursor: "pointer",
                            transition: "all 0.2s ease-in-out",
                            borderColor: isSelected ? "#2f3a88" : undefined,
                            borderWidth: isSelected ? "2px" : "1px"
                          }}
                        >
                          <div className="d-flex justify-content-center mb-3">
                            {doctor.doctorProfileImage?.image ? (
                              <img
                                src={`${doctor.doctorProfileImage?.imageMeta},${doctor.doctorProfileImage?.image}`}
                                alt={doctor.doctorName}
                                style={{ width: "90px", height: "90px", borderRadius: "50%", objectFit: "cover" }}
                              />
                            ) : (
                              <FaRegUserCircle size={80} color="#cbd5e1" />
                            )}
                          </div>
                          <h5 className="fw-bold mb-2" style={{ color: "#1e293b", fontSize: "1.05rem" }}>
                            {doctor.doctorName}
                          </h5>
                          <p className="text-muted small mb-1">
                            <i className="material-icons align-middle me-1" style={{ fontSize: "0.9rem" }}>phone</i>
                            {doctor.phoneNumber}
                          </p>
                          <p className="text-muted small mb-1">
                            <i className="material-icons align-middle me-1" style={{ fontSize: "0.9rem" }}>email</i>
                            {doctor.email}
                          </p>
                          <p className="text-muted small mb-0">
                            <i className="material-icons align-middle me-1" style={{ fontSize: "0.9rem" }}>location_on</i>
                            {doctor.address}, {doctor.city}, {doctor.pincode}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div className="d-flex justify-content-center mb-4">
                <Pagination>
                  <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                  <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                  {renderPageNumbers()}
                  <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages - 1} />
                  <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage === totalPages - 1} />
                </Pagination>
              </div>

              <div className="d-flex justify-content-end pt-3 border-top">
                <button
                  className="btn-hm-primary px-4"
                  onClick={() => setStep(2)}
                  disabled={!selectedDoctor}
                >
                  Next Step ➔
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="hm-card mb-4 col-md-8 mx-auto p-0">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">event_note</i>
                Book Doctor Appointment
              </h3>
            </div>

            <div className="hm-card-body p-4">
              {selectedDoctor && (
                <div>
                  <div className="p-3 bg-light rounded mb-4">
                    <p className="mb-1 small">
                      <strong className="text-dark">Selected Doctor:</strong> {selectedDoctor.doctorName} ({selectedDoctor.phoneNumber})
                    </p>
                    <p className="mb-0 small text-muted">
                      {selectedDoctor.address}, {selectedDoctor.city}
                    </p>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="appointmentDate" className="form-label small fw-semibold">
                      Select Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      id="appointmentDate"
                      className="form-control"
                      value={appointmentDate}
                      onChange={(e) => setAppointmentDate(e.target.value)}
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                  </div>
                </div>
              )}

              <div className="d-flex justify-content-between pt-4 border-top">
                <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => setStep(1)}>
                  ◄ Back
                </Button>
                <button className="btn-hm-primary px-4" onClick={handleBookAppointment}>
                  Book Appointment
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <AdminDashboard />
      {loader && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">
          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">medical_services</i>
                Book Doctor Appointment
              </h2>
              <p className="hm-page-subtitle">
                Select a qualified doctor and schedule a medical consultation.
              </p>
            </div>
          </div>

          {/* Stepper Header */}
          <div className="d-flex align-items-center justify-content-center gap-4 mb-4 pb-3 border-bottom">
            <div
              className={`d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold ${
                step === 1
                  ? "bg-primary text-white shadow-sm"
                  : "bg-light text-secondary border"
              }`}
            >
              <span
                className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${
                  step === 1 ? "bg-white text-primary" : "bg-secondary text-white"
                }`}
                style={{ width: "24px", height: "24px", fontSize: "12px" }}
              >
                1
              </span>
              <span>Select Doctor</span>
            </div>

            <div className="text-muted">➔</div>

            <div
              className={`d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold ${
                step === 2
                  ? "bg-primary text-white shadow-sm"
                  : "bg-light text-secondary border"
              }`}
            >
              <span
                className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${
                  step === 2 ? "bg-white text-primary" : "bg-secondary text-white"
                }`}
                style={{ width: "24px", height: "24px", fontSize: "12px" }}
              >
                2
              </span>
              <span>Book Appointment</span>
            </div>
          </div>

          {renderStepContent(step)}
        </div>
      </div>

      <Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} centered>
        <Modal.Header closeButton className="border-bottom px-4 py-3">
          <Modal.Title className="h6 fw-bold mb-0 text-primary">Appointment Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="small text-muted mb-2">
            Please ensure that the employee is fasting for at least 10 hours before taking the test.
          </p>
          {paymentRequiredResponse.requireEmpPayment === true && (
            <p className="small text-danger mb-0">
              Please note, in case of appointment cancellation, the charges of platform fee will not be refunded.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-top px-4 py-2">
          <Button variant="outline-secondary" className="rounded-pill btn-sm px-3 me-2" onClick={() => setShowWarningModal(false)}>
            Cancel
          </Button>
          <button className="btn-hm-primary px-3 py-1" onClick={confirmBookAppointment}>
            Confirm & Book
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default BookDoctorAppointment;

